/**
 * Inline shipping address picker for store checkout (saved rows + add-new form).
 */

import React, { useCallback, useRef, useState, useEffect } from 'react';
import { getShippingAddresses, addShippingAddress } from '../../helpers/storeHelpers';
import toast from 'react-hot-toast';
import ModalShell from '../../modals/ModalShell';

const ADDRESS_FIELDS = [
  { key: 'full_name', label: 'Full name', required: true },
  { key: 'street_address', label: 'Street address', required: true },
  { key: 'city', label: 'City', required: true },
  { key: 'state', label: 'State / Province', required: false },
  { key: 'postal_code', label: 'Postal code', required: true },
  { key: 'country', label: 'Country', required: true },
  { key: 'phone_number', label: 'Phone', required: false },
];

const KEYBOARD_RECENTER_DELAYS_MS = [100, 300, 600, 900, 1200];

function AddAddressForm({ onAdded, onCancel, scrollContainerRef }) {
  const [form, setForm] = useState({
    full_name: '',
    street_address: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'United States',
    phone_number: '',
  });
  const [saving, setSaving] = useState(false);
  const focusedFieldRef = useRef(null);
  const centerTimersRef = useRef([]);

  const handleFieldChange = (key, e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (focusedFieldRef.current === e.currentTarget) {
      scheduleCenterField(e.currentTarget);
    }
  };
  const clearCenterTimers = useCallback(() => {
    centerTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    centerTimersRef.current = [];
  }, []);
  const centerField = useCallback((field) => {
    if (!field) return;
    const scrollContainer = scrollContainerRef?.current;
    if (!scrollContainer) {
      field.scrollIntoView?.({ block: 'center', inline: 'nearest', behavior: 'smooth' });
      return;
    }
    const containerRect = scrollContainer.getBoundingClientRect();
    const fieldRect = field.getBoundingClientRect();
    const fieldTargetTop = containerRect.height * 0.35;
    const targetScrollTop =
      scrollContainer.scrollTop +
      fieldRect.top -
      containerRect.top -
      fieldTargetTop +
      fieldRect.height / 2;
    scrollContainer.scrollTop = Math.max(0, targetScrollTop);
  }, [scrollContainerRef]);
  const scheduleCenterField = useCallback((field) => {
    clearCenterTimers();
    centerField(field);
    centerTimersRef.current = KEYBOARD_RECENTER_DELAYS_MS.map((delay) =>
      window.setTimeout(() => centerField(field), delay)
    );
  }, [centerField, clearCenterTimers]);
  const centerFocusedField = (e) => {
    focusedFieldRef.current = e.currentTarget;
    scheduleCenterField(e.currentTarget);
  };

  useEffect(() => {
    const recenter = () => {
      scheduleCenterField(focusedFieldRef.current);
    };
    const viewport = window.visualViewport;
    viewport?.addEventListener?.('resize', recenter);
    viewport?.addEventListener?.('scroll', recenter);
    window.addEventListener('resize', recenter);
    return () => {
      clearCenterTimers();
      viewport?.removeEventListener?.('resize', recenter);
      viewport?.removeEventListener?.('scroll', recenter);
      window.removeEventListener('resize', recenter);
    };
  }, [clearCenterTimers, scheduleCenterField]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = ADDRESS_FIELDS.filter((f) => f.required && !form[f.key]?.trim());
    if (missing.length || saving) return;
    setSaving(true);
    try {
      const address = await addShippingAddress(form);
      toast.success('Address saved');
      onAdded(address);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {ADDRESS_FIELDS.map(({ key, label, required }) => {
        const inputId = `store-shipping-${key}`;
        return (
          <div key={key}>
            <label htmlFor={inputId} className="block text-ash text-sm font-medium mb-1">
              {label} {required && '*'}
            </label>
            <input
              id={inputId}
              type={key === 'phone_number' ? 'tel' : 'text'}
              inputMode={key === 'phone_number' ? 'tel' : undefined}
              value={form[key] || ''}
              onChange={(e) => handleFieldChange(key, e)}
              onFocus={centerFocusedField}
              onClick={centerFocusedField}
              placeholder={label}
              className="w-full px-4 py-2 rounded-xl bg-shadow border border-charcoal text-ash placeholder-silver text-sm focus:outline-none focus:ring-2 focus:ring-ether/50"
            />
          </div>
        );
      })}
      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2 rounded-xl border border-shadow text-ash text-sm font-medium hover:bg-shadow"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 py-2 rounded-xl bg-ether text-void text-sm font-medium disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save address'}
        </button>
      </div>
    </form>
  );
}

function ShippingAddressSection({ selectedAddress, onSelectAddress }) {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const addAddressBodyRef = useRef(null);

  useEffect(() => {
    getShippingAddresses()
      .then(setAddresses)
      .catch(() => setAddresses([]))
      .finally(() => setLoading(false));
  }, []);

  const handlePick = (address) => {
    onSelectAddress?.(address);
  };

  const handleAdded = (address) => {
    setAddresses((prev) => [...prev, address]);
    setShowAddModal(false);
    handlePick(address);
  };

  return (
    <div
      className="mt-4 space-y-3 border-t border-shadow pt-4"
      data-testid="store-shipping-section"
    >
      <h2 className="text-ash text-sm font-medium">Shipping address</h2>
      {loading ? (
        <p className="text-silver text-sm">Loading...</p>
      ) : (
        <>
          {addresses.length === 0 ? (
            <p className="text-silver text-sm">No saved addresses. Add one below.</p>
          ) : (
            <ul className="space-y-2">
              {addresses.map((addr) => {
                const isSelected = selectedAddress?.address_id === addr.address_id;
                return (
                  <li key={addr.address_id}>
                    <button
                      type="button"
                      onClick={() => handlePick(addr)}
                      aria-pressed={isSelected}
                      className={`w-full text-left p-3 rounded-xl border transition-colors ${
                        isSelected
                          ? 'border-ether bg-shadow ring-2 ring-ether/30'
                          : 'bg-shadow border-charcoal hover:border-ether/30'
                      }`}
                    >
                      <p className="font-medium text-ash text-sm">{addr.full_name}</p>
                      <p className="text-silver text-sm">
                        {addr.street_address}, {addr.city}
                        {addr.state ? `, ${addr.state}` : ''} {addr.postal_code}
                      </p>
                      {addr.is_default && (
                        <span className="inline-block mt-1 text-xs text-ether">Default</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="w-full py-2 rounded-xl border border-dashed border-shadow text-silver text-sm hover:border-ether/50 hover:text-ether transition-colors"
          >
            + Add new address
          </button>
          {showAddModal && (
            <ModalShell
              title="Add shipping address"
              onClose={() => setShowAddModal(false)}
              panelClassName="max-w-md max-h-full flex flex-col overflow-hidden"
            >
              <div
                ref={addAddressBodyRef}
                className="min-h-0 overflow-y-auto pr-1 pb-32 space-y-3"
                data-testid="shipping-address-modal-body"
              >
                <p className="text-silver text-xs">Add a new address to your profile.</p>
                <AddAddressForm
                  onAdded={handleAdded}
                  onCancel={() => setShowAddModal(false)}
                  scrollContainerRef={addAddressBodyRef}
                />
              </div>
            </ModalShell>
          )}
        </>
      )}
    </div>
  );
}

export default ShippingAddressSection;
